import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  MapPin, Upload, X, Check, ChevronRight, ChevronLeft, 
  Building, Phone, Clock, CreditCard, Heart, Briefcase, Camera,
  Globe, AlertCircle, Shield, ArrowRight, Navigation
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useModeration, MODERATION_CONTEXTS, MODERATION_ACTIONS, ModerationAlert } from '../hooks/useModeration';
import PasswordStrength, { isPasswordStrong } from '../components/PasswordStrength';
// Use centralized API service
import { 
  categoriesApi,
  municipiosApi,
  territoriesApi,
  authApi,
  streetsApi,
  api 
} from '../services/api';
import AddressInput from '../components/AddressInput';
import CepInput, { stripCep, isValidCepFormat } from '../components/CepInput';
import SEOHead from '../components/SEOHead';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import EspecializacoesAutocomplete from '../components/EspecializacoesAutocomplete';
import { api as apiClient } from '../services/api';

import StepIndicator from './cadastrar/StepIndicator';
import {
  CheckboxItem, FormInput, FormTextarea, FormSelect,
} from './cadastrar/FormComponents';
import {
  formatCpfCnpj, formatCpf, validateCpf, validateCnpj, validateCpfCnpj,
} from './cadastrar/documentValidators';
import { readLocalDraft } from './cadastrar/draftStorage';
import { STEP_LABELS, TOTAL_STEPS, DIAS_SEMANA } from './cadastrar/constants';
import { createDefaultBusinessForm } from './cadastrar/defaultForm';
import { composeEndereco } from './cadastrar/payloadHelpers';
import {
  hasRichTextContent,
  validateCurrentStep as validateStep,
  getMissingFields as computeMissingFields,
  findFirstIncompleteStep,
} from './cadastrar/stepValidators';
import useMunicipioFeatures from '../hooks/useMunicipioFeatures';

export default function Cadastrar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { municipio: municipioSlugParam } = useParams();
  const continueId = searchParams.get('continue');
  const { user, token, login: authLogin } = useAuth();
  const { location } = useLocation();

  const [authStep, setAuthStep] = useState(user ? false : true);
  const [restoredDraft] = useState(readLocalDraft);
  const [currentStep, setCurrentStep] = useState(() => (restoredDraft?.step > 1 ? restoredDraft.step : 1));
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [territorios, setTerritorios] = useState([]); // Territories for current municipality
  const [bairrosTerritorios, setBairrosTerritorios] = useState([]); // Bairros under selected territory
  const [subcategorias, setSubcategorias] = useState([]);
  const [availableTags, setAvailableTags] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cpfCnpjError, setCpfCnpjError] = useState('');
  const [createdBusinessId, setCreatedBusinessId] = useState(() => restoredDraft?.createdBusinessId || null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Auth form
  const [isLogin, setIsLogin] = useState(true);
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    nome: '',
    telefone: '',
    cpf: ''
  });
  const [authCpfError, setAuthCpfError] = useState('');

  // Business form - complete structure
  const defaultBusinessForm = createDefaultBusinessForm({
    latitude: location?.latitude || 0,
    longitude: location?.longitude || 0,
  });
  const [businessForm, setBusinessForm] = useState(() =>
    restoredDraft ? { ...defaultBusinessForm, ...restoredDraft.form, images: [], avatar_url: '', imagens: {} } : defaultBusinessForm
  );

  // Feature flags do município selecionado (delivery, "preciso de…" etc.).
  // Se a prefeitura desativou uma feature, escondemos os campos correspondentes
  // no cadastro para não coletar dados inúteis.
  const selectedMunicipioSlug =
    municipios.find((m) => m.id === businessForm.municipio_id)?.slug || municipioSlugParam || null;
  const { delivery_enabled: municipioDeliveryEnabled } = useMunicipioFeatures(selectedMunicipioSlug);

  // Vaga form for hiring section
  const [newVaga, setNewVaga] = useState({
    titulo: '',
    descricao: '',
    forma_candidatura: ''
  });

  // Moderation
  const { checkMultipleFields, hasBlockingIssues, isChecking: isCheckingModeration } = useModeration();
  const [moderationResults, setModerationResults] = useState({});

  const totalSteps = TOTAL_STEPS;
  const stepLabels = STEP_LABELS;

  useEffect(() => {
    loadCategories();
    loadMunicipios();
  }, []);

  // ---- Auto-save local do rascunho (a cada etapa/alteração) ----
  // Evita perda de dados se o usuário fechar o navegador no meio do cadastro.
  // A restauração acontece de forma síncrona nos inicializadores de estado
  // (restoredDraft) — aqui apenas notificamos o usuário.
  const localDraftKey = user?.email ? `cadastro_draft_${user.email}` : null;

  useEffect(() => {
    if (!restoredDraft || !localDraftKey) return;
    // pequeno delay: garante que o <Toaster> global já esteja montado
    const t = setTimeout(() => {
      toast.success('Rascunho recuperado! Continuando de onde você parou.', {
        duration: 8000,
        action: {
          label: 'Começar do zero',
          onClick: () => {
            localStorage.removeItem(localDraftKey);
            window.location.href = '/cadastrar';
          },
        },
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Salva (debounced) a cada alteração do formulário ou mudança de etapa
  useEffect(() => {
    if (!localDraftKey || authStep || continueId) return;
    const t = setTimeout(() => {
      try {
        // Base64 de imagens estoura a quota do localStorage — não persistir
        const { images, avatar_url, imagens, ...light } = businessForm;
        localStorage.setItem(localDraftKey, JSON.stringify({
          form: light,
          step: currentStep,
          createdBusinessId,
          savedAt: Date.now(),
        }));
      } catch { /* quota excedida — ignora */ }
    }, 800);
    return () => clearTimeout(t);
  }, [businessForm, currentStep, createdBusinessId, localDraftKey, authStep, continueId]);

  // Pré-preenchimento via query `?municipio_id=…` vindo do AuthModal (aba "Sou
  // empreendedor"). Aplica uma única vez quando a lista de municípios chega e
  // o formulário ainda não tem município escolhido.
  const prefilledMunicipioId = searchParams.get('municipio_id');
  useEffect(() => {
    if (
      prefilledMunicipioId
      && municipios.length > 0
      && !businessForm.municipio_id
      && municipios.some((m) => m.id === prefilledMunicipioId)
    ) {
      handleMunicipioChange(prefilledMunicipioId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledMunicipioId, municipios.length]);

  // Preenche o município automaticamente a partir do slug da URL
  // (/{municipio-slug}/cadastrar) — o usuário sempre cadastra na cidade
  // que está navegando. NÃO permitimos escolher outra cidade no wizard.
  useEffect(() => {
    if (!municipioSlugParam || municipios.length === 0 || businessForm.municipio_id) return;
    const match = municipios.find((m) => m.slug === municipioSlugParam);
    if (match) handleMunicipioChange(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioSlugParam, municipios.length]);

  // Convite via link: `?email=X&modo=cadastro` — vem do "Compartilhar link
  // de cadastro" da tela de transferência de responsável da ONG. Abre a
  // tela em modo "Criar conta" com o email pré-preenchido.
  const prefilledEmail = searchParams.get('email');
  const prefilledModo = searchParams.get('modo');
  useEffect(() => {
    if (prefilledEmail && !authForm.email) {
      setAuthForm((prev) => ({ ...prev, email: prefilledEmail }));
    }
    if (prefilledModo === 'cadastro') {
      setIsLogin(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledEmail, prefilledModo]);


  // Município resolvido pelo slug (usado para exibir "cidade fixa" no step 6).
  const fixedMunicipio = municipioSlugParam
    ? municipios.find((m) => m.slug === municipioSlugParam)
    : null;

  // Resume draft when ?continue={id} is present
  useEffect(() => {
    let cancelled = false;
    const loadDraft = async () => {
      if (!continueId || !token) return;
      try {
        const res = await api.get(`/businesses/${continueId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const draft = res.data;
        if (!draft || cancelled) return;
        // Refuse to load if it's already a completed cadastro
        if (draft.is_draft === false || (draft.aceita_termos_uso && draft.aceita_politica_privacidade)) {
          alert('Este cadastro já está concluído. Use a página "Editar Negócio" para atualizar.');
          navigate(`/editar-negocio/${draft.id}`);
          return;
        }
        // Pre-fill form preserving the keys we know about
        setBusinessForm((prev) => ({
          ...prev,
          ...draft,
          // Keep null-safe fields aligned with form state shape
          ano_fundacao: draft.ano_fundacao ? String(draft.ano_fundacao) : '',
          tags: draft.tags || [],
          tags_controladas_ids: draft.tags_controladas_ids || [],
          tags_livres_pendentes: draft.tags_livres_pendentes || [],
          categorias_secundarias: draft.categorias_secundarias || [],
          horarios: draft.horarios || prev.horarios,
          imagens: draft.imagens || {},
          images: draft.images || [],
        }));
        setCreatedBusinessId(draft.id);
        setCurrentStep(11); // jump straight to "Fotos"
        window.scrollTo(0, 0);
      } catch (err) {
        console.error('Erro ao retomar rascunho:', err);
        alert('Não foi possível carregar o rascunho. Você pode iniciar um novo cadastro.');
        navigate('/cadastrar', { replace: true });
      }
    };
    loadDraft();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueId, token]);

  // Descoberta de rascunho no servidor (iter254 — cross-device recovery).
  // Se o usuário está logado, abriu /cadastrar SEM ?continue= e tem rascunho
  // pendente no servidor, pergunta se quer continuar. Evita criar duplicatas
  // quando o localStorage do device atual está vazio.
  const [serverDraftOffer, setServerDraftOffer] = useState(null);
  useEffect(() => {
    if (!token || continueId || authStep) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/my-businesses/drafts', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const drafts = res.data?.items || [];
        if (drafts.length === 0) return;
        // Só oferece se o localStorage do device NÃO tiver rascunho salvo,
        // OU se o rascunho local for de um negócio diferente do servidor.
        const localHasSameDraft = restoredDraft?.createdBusinessId
          && drafts.some((d) => d.id === restoredDraft.createdBusinessId);
        if (localHasSameDraft) return;
        setServerDraftOffer(drafts[0]);
      } catch (err) {
        // silencioso: se falhar, o usuário só perde a oferta de recuperar
        console.warn('[Cadastrar] não pude verificar rascunhos remotos:', err?.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, continueId, authStep]);

  useEffect(() => {
    if (location) {
      setBusinessForm(prev => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude
      }));
    }
  }, [location]);

  const loadCategories = async () => {
    try {
      const data = await categoriesApi.list();
      // Filtra categorias arquivadas — novos negócios só devem ver a
      // taxonomia ativa (v2). Categorias com `bloqueada_para_novos_cadastros`
      // continuam existindo pros negócios já cadastrados nelas.
      const ativas = (Array.isArray(data) ? data : []).filter(
        (c) => !c?.bloqueada_para_novos_cadastros
      );
      setCategories(ativas);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadMunicipios = async () => {
    try {
      const data = await municipiosApi.list();
      setMunicipios(data);
    } catch (error) {
      console.error('Erro ao carregar municípios:', error);
    }
  };

  // Load level 1 territories (sede/distritos) for a municipality
  const loadTerritorios = async (municipioId) => {
    try {
      const data = await territoriesApi.list({ municipio_id: municipioId, level1_only: true });
      setTerritorios(data || []);
    } catch (error) {
      console.error('Erro ao carregar territórios:', error);
      setTerritorios([]);
    }
  };

  // Load bairros under a specific territory (sede or distrito)
  const loadBairrosTerritorios = async (parentId) => {
    try {
      const data = await territoriesApi.bairros(parentId);
      setBairrosTerritorios(data || []);
    } catch (error) {
      console.error('Erro ao carregar bairros:', error);
      setBairrosTerritorios([]);
    }
  };

  // Fix defensivo (iter 229): auto-carrega bairros da Sede quando o usuário
  // já escolheu zona Urbana/Rural mas `territorios` ainda estava vazio no
  // momento do onChange. Assim que a lista carrega, dispara o fetch.
  useEffect(() => {
    if (!businessForm.municipio_id) return;
    const zona = businessForm.tipo_localizacao;
    if (zona !== 'area_urbana' && zona !== 'comunidade_rural') return;
    if (!Array.isArray(territorios) || territorios.length === 0) return;
    const sede = territorios.find((t) => t.tipo === 'sede');
    if (!sede) return;
    // Se ainda não temos bairros carregados OU o distrito_id não bate com
    // a Sede atual, dispara o fetch.
    if (bairrosTerritorios.length === 0 || businessForm.distrito_id !== sede.id) {
      setBusinessForm((prev) => ({
        ...prev,
        distrito_id: sede.id,
        distrito: sede.nome,
        is_sede: true,
      }));
      loadBairrosTerritorios(sede.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessForm.tipo_localizacao, territorios, businessForm.municipio_id]);

  // Handle municipality change - load territories
  // ---- Localização flexível: geolocalização do navegador + geocode rural ----
  const [ruralGeoMsg, setRuralGeoMsg] = useState('');

  const handleUsarLocalizacaoAtual = () => {
    if (!navigator.geolocation) {
      toast.error('Seu navegador não suporta geolocalização.');
      return;
    }
    toast.info('Obtendo sua localização...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusinessForm((prev) => ({
          ...prev,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }));
        toast.success('Localização capturada! Confira o marcador no mapa.');
      },
      () => toast.error('Não foi possível obter sua localização. Verifique a permissão do navegador.'),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  // Geocode automático para comunidade rural (Comunidade + Distrito + Município)
  useEffect(() => {
    if (businessForm.tipo_localizacao !== 'comunidade_rural') { setRuralGeoMsg(''); return; }
    const comunidade = (businessForm.comunidade || '').trim();
    if (comunidade.length < 3 || !businessForm.municipio_id) return;
    const municipio = municipios.find((m) => m.id === businessForm.municipio_id);
    const municipioNome = municipio?.nome || '';
    const uf = municipio?.uf || 'MG';
    const t = setTimeout(async () => {
      try {
        // Use unified /api/geocodificar with progressive fallback + precisao_baixa flag.
        const res = await apiClient.post('/geocodificar', {
          tipo_localizacao: 'rural',
          distrito_nome: businessForm.distrito || 'Sede',
          comunidade_nome: comunidade,
          municipio_nome: municipioNome,
          estado_sigla: uf,
        });
        const data = res?.data;
        if (data?.lat && data?.lng) {
          setBusinessForm((prev) => ({
            ...prev,
            latitude: data.lat,
            longitude: data.lng,
            geocodificacao_origem: data.origem || 'comunidade_aproximada',
          }));
          setRuralGeoMsg(
            data.precisao_baixa
              ? '⚠️ Localização aproximada da comunidade. Ajuste o pin para a posição exata do seu negócio, se necessário.'
              : 'Localização encontrada! Confira o marcador no mapa e ajuste se necessário.'
          );
        } else {
          setRuralGeoMsg('Não conseguimos localizar automaticamente. Posicione o pin manualmente no mapa.');
        }
      } catch {
        setRuralGeoMsg('Não conseguimos localizar automaticamente. Posicione o pin manualmente no mapa.');
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessForm.comunidade, businessForm.distrito, businessForm.municipio_id, businessForm.tipo_localizacao]);

  const handleMunicipioChange = (municipioId) => {
    setBusinessForm(prev => ({
      ...prev,
      municipio_id: municipioId,
      territory_id: '',
      is_sede: null,
      distrito_id: '',
      bairro: ''
    }));
    setTerritorios([]);
    setBairrosTerritorios([]);
    
    if (municipioId) {
      loadTerritorios(municipioId);
    }
  };

  // Handle territory type change (sede vs distrito)
  const handleTerritoryTypeChange = (isSede) => {
    setBusinessForm(prev => ({
      ...prev,
      is_sede: isSede,
      distrito_id: '',
      territory_id: '',
      bairro: ''
    }));
    setBairrosTerritorios([]);
    
    if (isSede) {
      // Find sede territory and load its bairros
      const sede = territorios.find(t => t.tipo === 'sede');
      if (sede) {
        setBusinessForm(prev => ({ ...prev, territorio_pai_id: sede.id }));
        loadBairrosTerritorios(sede.id);
      }
    }
  };

  // Handle distrito selection
  const handleDistritoChange = (distritoId) => {
    setBusinessForm(prev => ({
      ...prev,
      distrito_id: distritoId,
      territory_id: '',
      bairro: ''
    }));
    setBairrosTerritorios([]);
    
    if (distritoId) {
      loadBairrosTerritorios(distritoId);
    }
  };

  // Handle bairro/territory selection
  const handleTerritorySelect = (territoryId) => {
    const territory = bairrosTerritorios.find(t => t.id === territoryId);
    setBusinessForm(prev => ({
      ...prev,
      territory_id: territoryId,
      bairro: territory?.nome || prev.bairro,
      // Deriva distrito_id do parent_id do território (sede/distrito).
      // Sem isso, negócios ficavam sem vínculo hierárquico.
      distrito_id: territory?.parent_id || prev.distrito_id,
    }));
  };

  const loadSubcategorias = async (categoriaId) => {
    try {
      const data = await categoriesApi.subcategories(categoriaId);
      setSubcategorias(data.subcategorias || data || []);
    } catch (error) {
      console.error('Erro ao carregar subcategorias:', error);
      setSubcategorias([]);
    }
  };

  const loadTags = async (categoriaId) => {
    try {
      const data = await categoriesApi.tags(categoriaId);
      setAvailableTags(data.grupos || data || {});
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      setAvailableTags({});
    }
  };

  // ⚙️ Sincroniza listas locais (subcategorias/tags) sempre que categoria for
  // definida (inclui restore de draft/localStorage). Sem isso, ao retomar
  // um rascunho e voltar para o step 1, os checkboxes de subcategoria e as
  // pills de tag ficavam VAZIOS porque só eram populados via `handleCategoriaChange`.
  // Bug reportado pelo usuário: "quando o rascunho tá salvo alguns selects
  // não funcionam mais quando a pessoa volta os passos".
  useEffect(() => {
    const cat = businessForm.categoria;
    if (!cat) return;
    if (subcategorias.length === 0) loadSubcategorias(cat);
    if (Object.keys(availableTags).length === 0) loadTags(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessForm.categoria]);

  // ⚙️ Mesmo fix defensivo pros selects hierárquicos de LOCALIZAÇÃO:
  // município → territórios (sede/distritos) → bairros.
  // Sem esses effects, ao retomar rascunho e voltar pro step 6:
  //  - o select de distrito ficava vazio (territorios = [])
  //  - o select de bairro ficava vazio (bairrosTerritorios = [])
  // porque as listas locais só eram populadas via handleMunicipioChange
  // e handleDistritoChange.
  useEffect(() => {
    if (businessForm.municipio_id && territorios.length === 0) {
      loadTerritorios(businessForm.municipio_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessForm.municipio_id]);

  useEffect(() => {
    if (businessForm.distrito_id && bairrosTerritorios.length === 0) {
      loadBairrosTerritorios(businessForm.distrito_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessForm.distrito_id]);

  const handleCategoriaChange = (categoriaId) => {
    setBusinessForm(prev => ({
      ...prev,
      categoria: categoriaId,
      subcategoria: '',
      subcategorias: [],  // multi-select (iter 214)
      tags: [],
      tags_controladas_ids: [],
      tags_livres_pendentes: [],
    }));
    if (categoriaId) {
      loadSubcategorias(categoriaId);
      loadTags(categoriaId);
    } else {
      setSubcategorias([]);
      setAvailableTags({});
    }
  };

  // Multi-select de subcategorias (iter 214). Mantém compat com o campo
  // legado `subcategoria` (single str) — sincroniza com o primeiro item
  // do array para que qualquer código que ainda leia `subcategoria`
  // funcione (backend, buscas, SEO).
  const MAX_SUBCATEGORIAS = 6; // espelha backend businesses_extracted.py:135
  const handleSubcategoriaToggle = (sub) => {
    setBusinessForm(prev => {
      const current = prev.subcategorias || [];
      const isRemoving = current.includes(sub);
      // Bloqueia adicionar além do limite (evita 422 do backend em runtime).
      if (!isRemoving && current.length >= MAX_SUBCATEGORIAS) {
        toast.error(`Você pode selecionar até ${MAX_SUBCATEGORIAS} subcategorias.`);
        return prev;
      }
      const next = isRemoving
        ? current.filter((s) => s !== sub)
        : [...current, sub];
      return {
        ...prev,
        subcategorias: next,
        subcategoria: next[0] || '',
      };
    });
  };

  const MAX_CATEGORIAS_SECUNDARIAS = 3; // espelha backend businesses_extracted.py:130
  const handleSecondaryCategory = (catId) => {
    setBusinessForm(prev => {
      const current = prev.categorias_secundarias;
      if (current.includes(catId)) {
        return { ...prev, categorias_secundarias: current.filter(c => c !== catId) };
      }
      if (catId === prev.categoria) {
        toast.error('Essa categoria já é a principal — escolha outras diferentes.');
        return prev;
      }
      if (current.length >= MAX_CATEGORIAS_SECUNDARIAS) {
        toast.error(`Você pode selecionar até ${MAX_CATEGORIAS_SECUNDARIAS} categorias secundárias.`);
        return prev;
      }
      return { ...prev, categorias_secundarias: [...current, catId] };
    });
  };

  const handleTagToggle = (tag) => {
    setBusinessForm(prev => {
      const current = prev.tags;
      if (current.includes(tag)) {
        return { ...prev, tags: current.filter(t => t !== tag) };
      } else {
        return { ...prev, tags: [...current, tag] };
      }
    });
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CPF for registration
    if (!isLogin && !validateCpf(authForm.cpf)) {
      setAuthCpfError('CPF inválido');
      return;
    }
    
    // Validate password strength for registration
    if (!isLogin && !isPasswordStrong(authForm.password)) {
      alert('A senha não atende aos requisitos mínimos de segurança. Verifique os critérios abaixo do campo de senha.');
      return;
    }
    
    setLoading(true);

    try {
      const data = isLogin
        ? { email: authForm.email, password: authForm.password }
        : authForm;

      const res = isLogin 
        ? await authApi.login(data) 
        : await authApi.register(data);

      // 2FA gate: login can return {requires_2fa:true, challenge_token, email_masked, phone_masked, channel}
      // instead of an access_token when the user enabled 2FA.
      if (isLogin && res?.requires_2fa) {
        const payload = {
          challenge_token: res.challenge_token,
          email_masked: res.email_masked,
          phone_masked: res.phone_masked,
          channel: res.channel || 'email',
          email: data.email,
          password: data.password, // stored only in sessionStorage (same tab) for resend convenience
        };
        try {
          sessionStorage.setItem('loginTwoFaPending', JSON.stringify(payload));
        } catch (_) { /* ignore */ }
        navigate('/verificar-2fa', { state: payload });
        return;
      }

      // 2FA setup forçado: admin sem 2FA cadastrado (FORCE_2FA_ADMIN=true).
      // Backend retorna setup_token curto e a UI redireciona pra habilitar TOTP.
      // Sem esse handler, o access_token vinha undefined e o admin passava
      // com sessão quebrada (ou bypassava 2FA).
      if (isLogin && res?.requires_2fa_setup) {
        try {
          sessionStorage.setItem('force2faSetup', JSON.stringify({
            setup_token: res.setup_token,
            email: res.email,
            role: res.role,
            nome: res.nome,
          }));
        } catch (_) { /* ignore */ }
        navigate('/admin/2fa-setup-obrigatorio', { state: res });
        return;
      }

      authLogin(res.access_token, res.user, res.refresh_token || null);
      // Novo comportamento (iter 227 - grace period 7d): NÃO redireciona mais
      // para /verificar-email após cadastro. O usuário pode navegar/cadastrar
      // negócio livremente. Persistimos o canal preferido para quando ele
      // clicar "Verificar agora" no banner amarelo.
      if (!isLogin && res.user && res.user.email_verificado === false) {
        localStorage.setItem('pending_verification_email', res.user.email);
        try {
          const preferSms = !!(data.telefone || authForm.telefone);
          localStorage.setItem('pending_verification_channel', preferSms ? 'sms' : 'email');
        } catch (_) { /* ignore */ }
      }
      setAuthStep(false);
    } catch (error) {
      console.error('Erro na autenticação:', error);
      alert(error.response?.data?.detail || 'Erro ao autenticar');
    } finally {
      setLoading(false);
    }
  };

  // Google Login handler
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const returnTo = window.location.origin + '/cadastrar';
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backend}/api/auth/google/start?return_to=${encodeURIComponent(returnTo)}`;
  };

  // Handle Google OAuth callback — recebe tokens via URL fragment (#token=...&refresh=...).
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('token');
      const refresh = params.get('refresh');
      const needsCompletion = params.get('needs_profile_completion') === '1';
      if (token) {
        handleGoogleToken(token, refresh, needsCompletion);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleToken = async (token, refreshToken, needsCompletion) => {
    setLoading(true);
    try {
      // Salva token primeiro pro axios usar no /auth/me
      localStorage.setItem('token', token);
      const user = await authApi.me();
      // IMPORTANTE: passar refresh_token, senão o próximo 401 dispara
      // auto-logout com "Sua sessão expirou". Bug reportado (iter 219).
      authLogin(token, user, refreshToken || null);

      // Limpa hash da URL
      window.history.replaceState(null, '', window.location.pathname);

      if (needsCompletion) {
        alert('Por favor, complete seu perfil informando seu CPF.');
      }

      setAuthStep(false);
    } catch (error) {
      console.error('Erro no login Google:', error);
      localStorage.removeItem('token');
      alert('Erro ao fazer login com Google');
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();

    if (!validateCurrentStep()) {
      setShowValidationErrors(true);
      const missing = getMissingFields();
      if (missing.length > 0) {
        toast.error(
          `Complete os campos obrigatórios antes de finalizar: ${missing.join(', ')}.`,
          { duration: 6000 }
        );
      }
      setTimeout(() => {
        document.getElementById('step-validation-errors')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setShowValidationErrors(false);

    // Validar aceite de gestão
    if (!businessForm.terms_manager_accepted) {
      alert('Você precisa confirmar que tem autorização para gerenciar este negócio.');
      return;
    }

    // Validar subcategoria (obrigatória quando categoria tem subcategorias)
    if (businessForm.categoria && subcategorias.length > 0 && (businessForm.subcategorias || []).length === 0) {
      alert('Selecione ao menos uma subcategoria. Isso ajuda clientes a encontrar seu negócio nas buscas.');
      // Scroll to the field for better UX
      document.querySelector('[data-testid="business-subcategorias-checklist"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);

    try {
      // Check moderation for text fields — a IA NUNCA impede o cadastro.
      // Se algo for sinalizado, o cadastro é salvo e vai para revisão manual
      // (regra do produto: falso-positivos não podem travar empreendedores).
      const fieldsToModerate = [
        { field: 'nome', text: businessForm.nome, context: MODERATION_CONTEXTS.BUSINESS_NAME },
        { field: 'nome_fantasia', text: businessForm.nome_fantasia, context: MODERATION_CONTEXTS.BUSINESS_NAME },
        { field: 'descricao_curta', text: businessForm.descricao_curta, context: MODERATION_CONTEXTS.BUSINESS_DESCRIPTION },
        { field: 'descricao', text: businessForm.descricao, context: MODERATION_CONTEXTS.BUSINESS_DESCRIPTION },
      ];

      const results = await checkMultipleFields(fieldsToModerate);
      setModerationResults(results);

      // Avisa o usuário se algo foi sinalizado — mas NÃO bloqueia o envio.
      if (hasBlockingIssues(results)) {
        const flaggedFields = Object.entries(results)
          .filter(([_, r]) => r.action === MODERATION_ACTIONS.BLOCKED)
          .map(([field, _]) => {
            const labels = { nome: 'Nome', nome_fantasia: 'Nome Fantasia', descricao_curta: 'Descrição Curta', descricao: 'Descrição' };
            return labels[field] || field;
          });

        toast.warning(
          `Alguns campos (${flaggedFields.join(', ')}) foram sinalizados pela IA e serão revisados manualmente. Seu cadastro será enviado normalmente.`,
          { duration: 8000 }
        );
      }

      // Prepare data
      const composedEndereco = composeEndereco(businessForm);

      const submitData = {
        ...businessForm,
        cep: stripCep(businessForm.cep),  // backend recebe apenas dígitos
        endereco: composedEndereco || businessForm.endereco,
        ano_fundacao: businessForm.ano_fundacao ? parseInt(businessForm.ano_fundacao) : null,
        telefone: businessForm.telefone_fixo || businessForm.whatsapp
      };

      // When updating an existing draft, do NOT send legacy 'images' or 'avatar_url'
      // fields — they are managed exclusively by the /upload/imagem endpoint and
      // including them would overwrite the new `imagens.*` structure.
      if (createdBusinessId) {
        delete submitData.images;
        delete submitData.avatar_url;
      }
      delete submitData.imagens; // not part of BusinessCreate; managed by upload endpoint

      // If a draft was already created when entering "Fotos" step,
      // finalize it with PUT (manager record + initial moderation already exist).
      let res;
      if (createdBusinessId) {
        res = await api.put(`/businesses/${createdBusinessId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const params = new URLSearchParams({
          manager_role: businessForm.manager_role,
          manager_relation: businessForm.manager_relation,
          terms_accepted: 'true'
        });
        if (businessForm.manager_relation === 'OUTRO' && businessForm.manager_relation_description) {
          params.append('manager_relation_description', businessForm.manager_relation_description);
        }
        res = await api.post(`/businesses?${params.toString()}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      const bizData = res.data;
      const modStatus = bizData?.moderation_status;

      // A IA NUNCA impede o cadastro (regra do produto fev/2026). Se o backend
      // ainda retornar rejected/blocked (por versão em cache), tratamos como
      // pending_review — dado já está salvo, admin decide manualmente.
      if (modStatus === 'approved') {
        toast.success('Negócio cadastrado e publicado com sucesso!');
      } else {
        // pending_review, rejected, blocked ou qualquer outro → mesma mensagem.
        toast.success(
          'Cadastro recebido! Seu negócio será revisado manualmente pela equipe e publicado em breve. Você receberá um aviso por e-mail.',
          { duration: 9000 }
        );
      }
      if (localDraftKey) localStorage.removeItem(localDraftKey);
      navigate('/perfil?tab=negocios');
    } catch (error) {
      console.error('Erro ao cadastrar negócio:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar negócio');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Imagem muito grande. Tamanho máximo: 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;

        if (type === 'avatar') {
          setBusinessForm(prev => ({ ...prev, avatar_url: base64String }));
        } else if (type === 'gallery') {
          if (businessForm.images.length >= 10) {
            alert('Você pode adicionar no máximo 10 imagens');
            return;
          }
          setBusinessForm(prev => ({
            ...prev,
            images: [...prev.images, base64String]
          }));
        }
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      alert('Erro ao fazer upload da imagem');
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setBusinessForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleHorarioChange = (index, field, value) => {
    setBusinessForm(prev => {
      const newHorarios = [...prev.horarios];
      newHorarios[index] = { ...newHorarios[index], [field]: value };
      return { ...prev, horarios: newHorarios };
    });
  };

  const addVaga = () => {
    if (!newVaga.titulo || !newVaga.descricao || !newVaga.forma_candidatura) {
      alert('Preencha todos os campos da vaga');
      return;
    }
    setBusinessForm(prev => ({
      ...prev,
      vagas: [...prev.vagas, { ...newVaga }]
    }));
    setNewVaga({ titulo: '', descricao: '', forma_candidatura: '' });
  };

  const removeVaga = (index) => {
    setBusinessForm(prev => ({
      ...prev,
      vagas: prev.vagas.filter((_, i) => i !== index)
    }));
  };

  const useCurrentLocation = () => {
    if (location) {
      setBusinessForm(prev => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude
      }));
      alert('Localização atualizada!');
    } else {
      alert('Permita acesso à sua localização');
    }
  };

  // Auto-saves the business as a draft when entering the "Fotos" step (11),
  // so the ImageUploader has an entidade_id to attach uploads to.
  // Final submit (step 13) uses PUT instead of POST when this id exists.
  const ensureBusinessExists = useCallback(async () => {
    if (createdBusinessId) return createdBusinessId;
    if (creatingDraft) return null;

    setCreatingDraft(true);
    let draftData; // exposto pro catch pra facilitar debug
    try {
      const composedEndereco = composeEndereco(businessForm);

      draftData = {
        ...businessForm,
        cep: stripCep(businessForm.cep),  // backend recebe apenas dígitos
        endereco: composedEndereco || businessForm.endereco,
        ano_fundacao: businessForm.ano_fundacao ? parseInt(businessForm.ano_fundacao) : null,
        telefone: businessForm.telefone_fixo || businessForm.whatsapp,
        // Drop image fields — uploads happen via ImageUploader after creation
        avatar_url: '',
        images: [],
      };
      delete draftData.imagens; // not part of BusinessCreate; managed by upload endpoint
      // Endpoint lightweight (iter275): aceita subset parcial, não dispara o
      // validador completo do BusinessCreate. O commit final (com validação
      // total) acontece no passo 13 via PUT /api/businesses/{id}.
      const res = await api.post('/businesses/drafts', draftData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const id = res.data?.id;
      if (id) setCreatedBusinessId(id);
      return id;
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      // Log detalhado no console pra debug via F12 → Console.
      console.error('[ensureBusinessExists] falhou:', {
        status,
        detail: d,
        response: err?.response?.data,
        payload: draftData,
      });
      let msg = 'Erro ao salvar rascunho. Confira os passos anteriores.';
      if (typeof d === 'string') {
        msg = d;
      } else if (Array.isArray(d) && d.length > 0) {
        // Pydantic v2 retorna array com {loc, msg, type}. Junta os 3 primeiros
        // pra o admin ver exatamente qual campo está errado.
        const items = d.slice(0, 3).map((it) => {
          const campo = (it?.loc || []).slice(1).join('.') || '(desconhecido)';
          return `• ${campo}: ${it?.msg || 'inválido'}`;
        });
        const extra = d.length > 3 ? `\n... e mais ${d.length - 3}` : '';
        msg = `Erro de validação nos seguintes campos:\n\n${items.join('\n')}${extra}\n\nVolte aos passos anteriores e revise.`;
      } else if (status === 500) {
        msg = 'Erro interno ao salvar. Aguarde alguns segundos e tente novamente. Se persistir, avise o suporte.';
      } else if (status === 401 || status === 403) {
        msg = 'Sua sessão expirou. Recarregue a página e faça login novamente.';
      }
      alert(msg);
      return null;
    } finally {
      setCreatingDraft(false);
    }
  }, [createdBusinessId, creatingDraft, businessForm, token]);

  const nextStep = async () => {
    if (!validateCurrentStep()) {
      setShowValidationErrors(true);
      // Toast explícito com a lista dos campos que faltam — visível mesmo se
      // o usuário estiver com scroll longe do painel vermelho.
      const missing = getMissingFields();
      if (missing.length > 0) {
        toast.error(
          `Complete os campos obrigatórios antes de avançar: ${missing.join(', ')}.`,
          { duration: 6000 }
        );
      }
      setTimeout(() => {
        document.getElementById('step-validation-errors')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setShowValidationErrors(false);
    if (currentStep < totalSteps) {
      // Auto-create draft when moving INTO step 11 (Fotos)
      if (currentStep === 10 && !createdBusinessId) {
        // Iter275: rascunho agora é lightweight (backend aceita subset parcial),
        // então NÃO bloqueamos a progressão se algum passo anterior tem campo
        // faltante. Só avisamos com toast pra o usuário saber o que revisar
        // antes do commit final no passo 13.
        const failing = findFirstIncompleteStep(currentStep, businessForm);
        if (failing) {
          const stepLabel = STEP_LABELS[failing.step - 1] || `Passo ${failing.step}`;
          toast.warning(
            `Pendências no passo "${stepLabel}": ${failing.missing.join(', ')}. ` +
            `Você pode continuar — vamos salvar o rascunho, mas revise antes de finalizar.`,
            { duration: 10000 },
          );
        }
        const id = await ensureBusinessExists();
        if (!id) return; // abort step change if draft creation failed
      }
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setShowValidationErrors(false);
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const validateCurrentStep = () => validateStep(currentStep, businessForm);

  /**
   * Lista campos obrigatórios faltando no step atual — usado para feedback
   * visual abaixo do botão "Próximo" (em vez de só desabilitar silenciosamente).
   * Retorna [] quando o step está válido.
   */
  const getMissingFields = () => computeMissingFields(currentStep, businessForm);

  const diasSemana = DIAS_SEMANA;

  // Auth step render
  if (authStep) {
    return (
      <div className="min-h-screen bg-[var(--background)] pb-32 pt-8">
        <SEOHead title="Cadastrar Negócio" noindex={true} />
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
              {isLogin ? 'Entrar' : 'Criar conta'}
            </h1>
            <p className="text-[var(--text-secondary)] mb-6">
              {isLogin ? 'Entre para cadastrar seu negócio' : 'Crie uma conta para começar'}
            </p>

            {/* Google Login Button */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 border border-[var(--border)] bg-white hover:bg-gray-50 rounded-full px-6 py-3 font-medium transition-all mb-4"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--border)]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[var(--text-muted)]">ou</span>
              </div>
            </div>

            <form onSubmit={handleAuthSubmit} className="space-y-4">
              <FormInput
                label="Email"
                type="email"
                value={authForm.email}
                onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                required
                data-testid="auth-email-input"
              />

              <FormInput
                label="Senha"
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                required
                data-testid="auth-password-input"
              />
              {!isLogin && <PasswordStrength password={authForm.password} />}

              {!isLogin && (
                <>
                  <FormInput
                    label="Nome completo"
                    type="text"
                    value={authForm.nome}
                    onChange={(e) => setAuthForm({ ...authForm, nome: e.target.value })}
                    required
                  />
                  <FormInput
                    label="Telefone"
                    type="tel"
                    value={authForm.telefone}
                    onChange={(e) => setAuthForm({ ...authForm, telefone: e.target.value })}
                    required
                  />
                  
                  {/* CPF Field - Required for registration */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      CPF <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={authForm.cpf}
                      onChange={(e) => {
                        const formatted = formatCpf(e.target.value);
                        setAuthForm({ ...authForm, cpf: formatted });
                        if (authCpfError) setAuthCpfError('');
                      }}
                      onBlur={() => {
                        if (authForm.cpf && !validateCpf(authForm.cpf)) {
                          setAuthCpfError('CPF inválido');
                        }
                      }}
                      placeholder="000.000.000-00"
                      required
                      className={`w-full px-4 py-3 rounded-xl border ${
                        authCpfError 
                          ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' 
                          : 'border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]'
                      } transition-all`}
                      data-testid="auth-cpf-input"
                    />
                    {authCpfError && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {authCpfError}
                      </p>
                    )}
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-6 py-3 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="auth-submit-button"
              >
                {loading ? 'Carregando...' : (isLogin ? 'Entrar' : 'Criar conta')}
              </button>

              {isLogin && (
                <div className="text-center mt-3">
                  <a
                    href="/esqueci-senha"
                    className="text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Esqueci minha senha
                  </a>
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-[var(--primary)] hover:underline text-sm font-medium"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Não tem conta? Criar conta' : 'Já tem conta? Entrar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      // Step 1: Informações Básicas
      case 1:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Building className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Informações Básicas</h3>
                <p className="text-sm text-[var(--text-secondary)]">Dados principais do seu negócio</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                CNPJ ou CPF <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={businessForm.cnpj_cpf}
                onChange={(e) => {
                  const formatted = formatCpfCnpj(e.target.value);
                  setBusinessForm({ ...businessForm, cnpj_cpf: formatted });
                  // Clear error while typing
                  if (cpfCnpjError) setCpfCnpjError('');
                }}
                onBlur={() => {
                  if (businessForm.cnpj_cpf && !validateCpfCnpj(businessForm.cnpj_cpf)) {
                    const digits = businessForm.cnpj_cpf.replace(/\D/g, '');
                    if (digits.length === 11) {
                      setCpfCnpjError('CPF inválido');
                    } else if (digits.length === 14) {
                      setCpfCnpjError('CNPJ inválido');
                    } else {
                      setCpfCnpjError('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido');
                    }
                  } else {
                    setCpfCnpjError('');
                  }
                }}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                className={`w-full px-4 py-3 rounded-xl border ${
                  cpfCnpjError 
                    ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' 
                    : 'border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]'
                } transition-all`}
                data-testid="business-cnpj-input"
              />
              {cpfCnpjError && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {cpfCnpjError}
                </p>
              )}
              {/* Item #3: helper text LGPD-aware sobre dados pessoais agregados */}
              <p className="text-xs text-[var(--text-muted)] mt-2 leading-relaxed" data-testid="personal-data-lgpd-helper">
                Essas informações ajudam o município a entender melhor o perfil dos empreendedores e criar políticas de apoio adequadas. Seus dados são tratados de forma agregada.
              </p>
            </div>

            <FormInput
              label="Nome do negócio / Nome fantasia"
              type="text"
              value={businessForm.nome}
              onChange={(e) => setBusinessForm({ ...businessForm, nome: e.target.value })}
              required
              data-testid="business-nome-input"
            />

            <FormTextarea
              label="Descrição curta"
              value={businessForm.descricao_curta}
              onChange={(e) => setBusinessForm({ ...businessForm, descricao_curta: e.target.value })}
              placeholder="Uma frase que resume seu negócio (até 250 caracteres)"
              rows={2}
              maxLength={250}
              required
            />
            <p className="text-xs text-[var(--text-muted)] -mt-4">
              {businessForm.descricao_curta.length}/250 caracteres
            </p>

            <div>
              <label className="block text-sm font-medium mb-2">
                Descrição detalhada <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={businessForm.descricao}
                onChange={(html) => setBusinessForm({ ...businessForm, descricao: html })}
                testId="business-descricao-rich"
              />
              <p className="text-xs text-[var(--text-muted)] mt-2">
                Descreva produtos, serviços, diferenciais e experiência. Mínimo recomendado: 2 parágrafos.
              </p>
            </div>

            <FormSelect
              label="Categoria principal"
              value={businessForm.categoria}
              onChange={(e) => handleCategoriaChange(e.target.value)}
              required
              data-testid="business-categoria-select"
            >
              <option value="">Selecione uma categoria</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.nome}</option>
              ))}
            </FormSelect>

            {businessForm.categoria === 'publicos' && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4" data-testid="osc-redirect-alert">
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-purple-900">
                      É uma ONG, associação, coletivo ou projeto social?
                    </p>
                    <p className="text-sm text-purple-800 mt-1">
                      Organizações sociais têm um cadastro próprio, com perfil público dedicado,
                      formas de apoio (doações, voluntariado) e moderação pela gestão municipal.
                      Este formulário é exclusivo para <strong>negócios</strong>.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/social/cadastrar-organizacao')}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition-colors"
                      data-testid="osc-redirect-button"
                    >
                      Cadastrar Organização Social
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {businessForm.categoria && subcategorias.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Subcategorias <span className="text-red-500">*</span>
                  <span className="ml-2 text-xs text-gray-500 font-normal">
                    (selecione uma ou mais)
                  </span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Ajuda clientes a encontrar seu negócio por buscas específicas e melhora o ranqueamento no Google.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="business-subcategorias-checklist">
                  {subcategorias.map((sub) => {
                    const selected = (businessForm.subcategorias || []).includes(sub);
                    return (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => handleSubcategoriaToggle(sub)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          selected
                            ? 'bg-[var(--primary)] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`subcat-chip-${sub.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {selected ? '✓ ' : ''}{sub}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {businessForm.categoria && (businessForm.subcategorias || []).length > 0 && (
              <EspecializacoesAutocomplete
                subcategoriaId={`${businessForm.categoria}__${((businessForm.subcategorias || [])[0] || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                categoriaId={businessForm.categoria}
                valueControladas={businessForm.tags_controladas_ids || []}
                valueLivres={businessForm.tags_livres_pendentes || []}
                onChangeControladas={(ids) => setBusinessForm(prev => ({ ...prev, tags_controladas_ids: ids }))}
                onChangeLivres={(livres) => setBusinessForm(prev => ({ ...prev, tags_livres_pendentes: livres }))}
                maxControladas={8}
                maxLivres={3}
                api={apiClient}
                testIdPrefix="business-esp"
              />
            )}

            <FormInput
              label="Ano de fundação"
              type="number"
              value={businessForm.ano_fundacao}
              onChange={(e) => setBusinessForm({ ...businessForm, ano_fundacao: e.target.value })}
              placeholder="Ex: 2020"
              min="1900"
              max={new Date().getFullYear()}
            />
          </div>
        );

      // Step 2: Perfil Econômico
      case 2:
        const temCnpj = businessForm.cnpj_cpf.replace(/\D/g, '').length === 14;
        const isRendaPrincipal = businessForm.situacao_renda === 'principal';
        
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Briefcase className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Perfil Econômico</h3>
                <p className="text-sm text-[var(--text-secondary)]">Informações para políticas públicas</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Essas informações ajudam o município a entender melhor o perfil dos empreendedores e criar políticas de apoio adequadas. Seus dados são tratados de forma agregada.
              </p>
            </div>

            {temCnpj ? (
              // Campos para negócios com CNPJ
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building className="w-5 h-5 text-[var(--primary)]" />
                    Porte do Negócio
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tipo de empresa <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'mei', label: 'MEI - Microempreendedor Individual' },
                        { value: 'microempresa', label: 'Microempresa (ME)' },
                        { value: 'epp', label: 'Empresa de Pequeno Porte (EPP)' },
                        { value: 'media', label: 'Média Empresa' },
                        { value: 'grande', label: 'Grande Empresa' }
                      ].map(option => (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            businessForm.tipo_empresa === option.value
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="tipo_empresa"
                            value={option.value}
                            checked={businessForm.tipo_empresa === option.value}
                            onChange={(e) => setBusinessForm({ ...businessForm, tipo_empresa: e.target.value, tem_cnpj: true })}
                            className="w-5 h-5 text-[var(--primary)]"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Campos para negócios informais
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-[var(--primary)]" />
                    Situação de Renda e Ocupação
                  </h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Esse negócio é: <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'principal', label: 'Minha principal fonte de renda' },
                          { value: 'complemento', label: 'Um complemento de renda' }
                        ].map(option => (
                          <label
                            key={option.value}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              businessForm.situacao_renda === option.value
                                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="situacao_renda"
                              value={option.value}
                              checked={businessForm.situacao_renda === option.value}
                              onChange={(e) => setBusinessForm({ ...businessForm, situacao_renda: e.target.value, tem_cnpj: false })}
                              className="w-5 h-5 text-[var(--primary)]"
                            />
                            <span className="text-sm">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Sua situação atual é: <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'emprego_formal', label: 'Tenho emprego formal' },
                          { value: 'aposentado', label: 'Sou aposentado(a)' },
                          { value: 'sem_emprego', label: 'Não tenho emprego formal atualmente' },
                          { value: 'outro', label: 'Outro' }
                        ].map(option => (
                          <label
                            key={option.value}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              businessForm.situacao_ocupacao === option.value
                                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="situacao_ocupacao"
                              value={option.value}
                              checked={businessForm.situacao_ocupacao === option.value}
                              onChange={(e) => setBusinessForm({ ...businessForm, situacao_ocupacao: e.target.value })}
                              className="w-5 h-5 text-[var(--primary)]"
                            />
                            <span className="text-sm">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campos adicionais para renda principal */}
                {isRendaPrincipal && (
                  <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-[var(--primary)]" />
                      Sobre a equipe do negócio
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Quantidade de colaboradores diretos <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Pessoas que trabalham diretamente no negócio</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { value: '0', label: 'Nenhum' },
                          { value: '1', label: '1' },
                          { value: '2-3', label: '2 a 3' },
                          { value: '4-5', label: '4 a 5' },
                          { value: '6-10', label: '6 a 10' },
                          { value: '10+', label: 'Mais de 10' }
                        ].map(option => (
                          <label
                            key={option.value}
                            className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                              businessForm.colaboradores_diretos === option.value
                                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="colaboradores_diretos"
                              value={option.value}
                              checked={businessForm.colaboradores_diretos === option.value}
                              onChange={(e) => setBusinessForm({ ...businessForm, colaboradores_diretos: e.target.value })}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Quantidade de colaboradores indiretos <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Fornecedores, parceiros, freelancers</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { value: '0', label: 'Nenhum' },
                          { value: '1', label: '1' },
                          { value: '2-3', label: '2 a 3' },
                          { value: '4-5', label: '4 a 5' },
                          { value: '6-10', label: '6 a 10' },
                          { value: '10+', label: 'Mais de 10' }
                        ].map(option => (
                          <label
                            key={option.value}
                            className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                              businessForm.colaboradores_indiretos === option.value
                                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="colaboradores_indiretos"
                              value={option.value}
                              checked={businessForm.colaboradores_indiretos === option.value}
                              onChange={(e) => setBusinessForm({ ...businessForm, colaboradores_indiretos: e.target.value })}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Possui contratação sazonal?
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Contrata pessoas em períodos específicos do ano</p>
                      <div className="flex gap-3">
                        {[
                          { value: true, label: 'Sim' },
                          { value: false, label: 'Não' }
                        ].map(option => (
                          <label
                            key={String(option.value)}
                            className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              businessForm.contratacao_sazonal === option.value
                                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="contratacao_sazonal"
                              checked={businessForm.contratacao_sazonal === option.value}
                              onChange={() => setBusinessForm({ ...businessForm, contratacao_sazonal: option.value })}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {businessForm.contratacao_sazonal && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Em quais períodos costuma contratar?
                        </label>
                        <input
                          type="text"
                          value={businessForm.periodos_sazonais}
                          onChange={(e) => setBusinessForm({ ...businessForm, periodos_sazonais: e.target.value })}
                          placeholder="Ex: Festas de fim de ano, Carnaval, Páscoa..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      // Step 3: Como Atende aos Clientes
      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Globe className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Como Atende aos Clientes <span className="text-red-500">*</span></h3>
                <p className="text-sm text-[var(--text-secondary)]">Selecione pelo menos uma forma de atendimento do seu negócio</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CheckboxItem
                id="loja_fisica"
                label="Loja física"
                icon="🏪"
                checked={businessForm.loja_fisica}
                onChange={(e) => setBusinessForm({ ...businessForm, loja_fisica: e.target.checked })}
              />
              <CheckboxItem
                id="atendimento_no_meu_local"
                label="Atendimento no meu local"
                icon="🏠"
                checked={businessForm.atendimento_no_meu_local}
                onChange={(e) => setBusinessForm({ ...businessForm, atendimento_no_meu_local: e.target.checked })}
              />
              <CheckboxItem
                id="atendimento_no_local_cliente"
                label="Atendimento no local do cliente"
                icon="📍"
                checked={businessForm.atendimento_no_local_cliente}
                onChange={(e) => setBusinessForm({ ...businessForm, atendimento_no_local_cliente: e.target.checked })}
              />
              <CheckboxItem
                id="atendimento_com_agendamento"
                label="Atendimento com agendamento"
                icon="📅"
                checked={businessForm.atendimento_com_agendamento}
                onChange={(e) => setBusinessForm({ ...businessForm, atendimento_com_agendamento: e.target.checked })}
              />
              <CheckboxItem
                id="atendimento_online"
                label="Atendimento online"
                icon="💻"
                checked={businessForm.atendimento_online}
                onChange={(e) => setBusinessForm({ ...businessForm, atendimento_online: e.target.checked })}
              />
              {municipioDeliveryEnabled !== false && (
                <CheckboxItem
                  id="delivery"
                  label="Delivery"
                  icon="🛵"
                  checked={businessForm.delivery}
                  onChange={(e) => setBusinessForm({ ...businessForm, delivery: e.target.checked })}
                />
              )}
              <CheckboxItem
                id="retirada_no_local"
                label="Retirada no local"
                icon="🛍️"
                checked={businessForm.retirada_no_local}
                onChange={(e) => setBusinessForm({ ...businessForm, retirada_no_local: e.target.checked })}
              />
              <CheckboxItem
                id="entrega_combinada"
                label="Entrega combinada"
                icon="🤝"
                checked={businessForm.entrega_combinada}
                onChange={(e) => setBusinessForm({ ...businessForm, entrega_combinada: e.target.checked })}
              />
              <CheckboxItem
                id="itinerante"
                label="Atendimento itinerante ou ambulante"
                icon="🚚"
                checked={businessForm.itinerante}
                onChange={(e) => setBusinessForm({ ...businessForm, itinerante: e.target.checked })}
              />
            </div>

            <FormTextarea
              label="Área de cobertura"
              value={businessForm.area_cobertura}
              onChange={(e) => setBusinessForm({ ...businessForm, area_cobertura: e.target.value })}
              placeholder="Bairros atendidos para delivery ou serviços..."
              rows={3}
            />

            {/* Ocultar endereço */}
            <div className={`border-2 rounded-xl p-4 transition-all ${businessForm.ocultar_endereco ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
              <label className="flex items-start gap-3 cursor-pointer" htmlFor="ocultar_endereco">
                <input
                  type="checkbox"
                  id="ocultar_endereco"
                  checked={businessForm.ocultar_endereco}
                  onChange={(e) => setBusinessForm({ ...businessForm, ocultar_endereco: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded accent-[var(--primary)]"
                  data-testid="ocultar-endereco-checkbox"
                />
                <div>
                  <span className="font-medium text-sm">Não desejo exibir meu endereço publicamente</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Apenas a geolocalização do bairro será exibida no site. O endereço completo ficará oculto.
                  </p>
                </div>
              </label>
            </div>

            {!(businessForm.loja_fisica || businessForm.atendimento_no_meu_local || businessForm.atendimento_no_local_cliente || businessForm.atendimento_com_agendamento || businessForm.atendimento_online || businessForm.delivery || businessForm.retirada_no_local || businessForm.entrega_combinada || businessForm.itinerante) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3" data-testid="atendimento-warning">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Selecione pelo menos uma opção de atendimento para continuar.
                </p>
              </div>
            )}
          </div>
        );

      // Step 4: Contato & Digital
      case 4:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Phone className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Contato & Digital</h3>
                <p className="text-sm text-[var(--text-secondary)]">Como os clientes podem entrar em contato</p>
              </div>
            </div>

            <FormInput
              label="WhatsApp"
              type="tel"
              value={businessForm.whatsapp}
              onChange={(e) => setBusinessForm({ ...businessForm, whatsapp: e.target.value })}
              placeholder="5531999999999"
              required
              data-testid="business-whatsapp-input"
            />

            <FormInput
              label="Telefone fixo"
              type="tel"
              value={businessForm.telefone_fixo}
              onChange={(e) => setBusinessForm({ ...businessForm, telefone_fixo: e.target.value })}
              placeholder="(31) 3333-3333"
            />

            <FormInput
              label="E-mail"
              type="email"
              value={businessForm.email}
              onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
              placeholder="contato@seunegocio.com"
            />

            <FormInput
              label="Site oficial"
              type="url"
              value={businessForm.site}
              onChange={(e) => setBusinessForm({ ...businessForm, site: e.target.value })}
              placeholder="https://www.seunegocio.com"
            />

            <FormInput
              label="Loja online / Marketplace"
              type="url"
              value={businessForm.loja_online}
              onChange={(e) => setBusinessForm({ ...businessForm, loja_online: e.target.value })}
              placeholder="https://www.mercadolivre.com/sua-loja"
            />

            <FormInput
              label="Instagram"
              type="text"
              value={businessForm.instagram}
              onChange={(e) => setBusinessForm({ ...businessForm, instagram: e.target.value })}
              placeholder="@seunegocio"
            />
          </div>
        );

      // Step 5: Funcionamento
      case 5:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Clock className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Funcionamento</h3>
                <p className="text-sm text-[var(--text-secondary)]">Horários de atendimento</p>
              </div>
            </div>

            <div className="space-y-3">
              {businessForm.horarios.map((horario, index) => (
                <div key={horario.dia} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-28">
                    <span className="text-sm font-medium">{diasSemana[horario.dia]}</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={horario.fechado}
                      onChange={(e) => handleHorarioChange(index, 'fechado', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-600">Fechado</span>
                  </label>
                  {!horario.fechado && (
                    <>
                      <input
                        type="time"
                        value={horario.abre}
                        onChange={(e) => handleHorarioChange(index, 'abre', e.target.value)}
                        className="px-2 py-1 rounded border border-gray-300 text-sm"
                      />
                      <span className="text-gray-400">até</span>
                      <input
                        type="time"
                        value={horario.fecha}
                        onChange={(e) => handleHorarioChange(index, 'fecha', e.target.value)}
                        className="px-2 py-1 rounded border border-gray-300 text-sm"
                      />
                    </>
                  )}
                </div>
              ))}
            </div>

            <FormTextarea
              label="Horários especiais"
              value={businessForm.horarios_especiais}
              onChange={(e) => setBusinessForm({ ...businessForm, horarios_especiais: e.target.value })}
              placeholder="Feriados, datas sazonais, eventos especiais..."
              rows={2}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CheckboxItem
                id="apenas_agendamento"
                label="Apenas com agendamento"
                icon="📅"
                checked={businessForm.apenas_agendamento}
                onChange={(e) => setBusinessForm({ ...businessForm, apenas_agendamento: e.target.checked })}
              />
              <CheckboxItem
                id="emergencial_24h"
                label="Atendimento 24h / Emergencial"
                icon="🚨"
                checked={businessForm.emergencial_24h}
                onChange={(e) => setBusinessForm({ ...businessForm, emergencial_24h: e.target.checked })}
              />
            </div>
          </div>
        );

      // Step 6: Localização
      case 6:
        const distritos = territorios.filter(t => t.tipo === 'distrito');
        const temSede = territorios.some(t => t.tipo === 'sede');
        const temDistritos = distritos.length > 0;
        
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <MapPin className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Localização</h3>
                <p className="text-sm text-[var(--text-secondary)]">Endereço do seu negócio</p>
              </div>
            </div>

            {/* 1. Municipality — FIXO a partir do slug da URL. O usuário
                 cadastra sempre na cidade em que está navegando; para trocar
                 basta ir a outra cidade no seletor do topo. */}
            {fixedMunicipio ? (
              <div
                className="p-4 rounded-xl border-2 border-[var(--primary)]/20 bg-[var(--primary)]/5 flex items-center gap-3"
                data-testid="fixed-municipio-card"
              >
                <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[var(--primary)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-medium">
                    Cadastrando em
                  </p>
                  <p className="font-bold text-base leading-tight">
                    {fixedMunicipio.nome} / {fixedMunicipio.uf}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-xs text-[var(--primary)] underline hover:opacity-80 flex-shrink-0"
                  data-testid="change-city-link"
                >
                  Trocar cidade
                </button>
              </div>
            ) : (
              <FormSelect
                label="Município/Cidade"
                value={businessForm.municipio_id}
                onChange={(e) => handleMunicipioChange(e.target.value)}
                required
                data-testid="municipio-select"
              >
                <option value="">Selecione o município</option>
                {municipios.map((mun) => (
                  <option key={mun.id} value={mun.id}>
                    {mun.nome}
                  </option>
                ))}
              </FormSelect>
            )}

            {/* Tipo de localização — Zona. Define a UX subsequente:
                Urbana → CEP obrigatório + ViaCEP + auto-geocoding
                Rural  → CEP obrigatório (rua opcional) + "Usar minha localização"
                Distrito → CEP + rua obrigatórios, dentro de um distrito não-sede */}
            <FormSelect
              label="Zona"
              value={businessForm.tipo_localizacao}
              onChange={(e) => {
                const zona = e.target.value;
                setBusinessForm({
                  ...businessForm,
                  tipo_localizacao: zona,
                  is_sede: zona === 'distrito' ? false : null,
                  distrito_id: '',
                  distrito: '',
                  territory_id: '',
                  bairro: '',
                  comunidade: '',
                });
                setBairrosTerritorios([]);
                // Para zonas Urbana/Rural, auto-seleciona a Sede como distrito
                // implícito (o(a) empreendedor(a) não precisa escolher).
                if (zona === 'area_urbana' || zona === 'comunidade_rural') {
                  const sede = territorios.find((t) => t.tipo === 'sede');
                  if (sede) {
                    setBusinessForm((prev) => ({
                      ...prev,
                      tipo_localizacao: zona,
                      distrito_id: sede.id,
                      distrito: sede.nome,
                      is_sede: true,
                    }));
                    loadBairrosTerritorios(sede.id);
                  }
                }
              }}
              required
              data-testid="tipo-localizacao-select"
            >
              <option value="area_urbana">Área urbana</option>
              <option value="comunidade_rural">Comunidade rural</option>
              <option value="distrito">Distrito</option>
            </FormSelect>

            {/* Aviso quando o município não tem Sede cadastrada (necessária
                para zonas Urbana e Rural). */}
            {(businessForm.tipo_localizacao === 'area_urbana' || businessForm.tipo_localizacao === 'comunidade_rural')
              && businessForm.municipio_id
              && territorios.length > 0
              && !territorios.find((t) => t.tipo === 'sede') && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="no-sede-warning">
                Este município ainda não tem a <strong>Sede</strong> cadastrada.
                Peça à prefeitura para configurar em <strong>Painel Admin → Territórios</strong>.
              </div>
            )}

            {/* Distrito — SÓ aparece quando a zona é "distrito" */}
            {businessForm.tipo_localizacao === 'distrito'
              && businessForm.municipio_id
              && territorios.length > 0 && (() => {
              const opcoes = territorios.filter((t) => t.tipo === 'distrito');
              if (opcoes.length === 0) {
                return (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="no-distritos-warning">
                    Este município ainda não tem <strong>distritos</strong> cadastrados.
                    Escolha &ldquo;Área urbana&rdquo; ou peça à prefeitura para cadastrar em <strong>Painel Admin → Territórios</strong>.
                  </div>
                );
              }
              return (
                <FormSelect
                  label="Distrito"
                  value={businessForm.distrito_id}
                  onChange={(e) => {
                    const chosen = opcoes.find((t) => t.id === e.target.value);
                    handleDistritoChange(e.target.value);
                    setBusinessForm((prev) => ({
                      ...prev,
                      distrito: chosen?.nome || '',
                      is_sede: false,
                    }));
                  }}
                  required
                  data-testid="distrito-select"
                >
                  <option value="">Selecione o distrito</option>
                  {opcoes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </FormSelect>
              );
            })()}

            {/* Bairros/Comunidades filhos do distrito escolhido, filtrados por zona.
                Regra:
                  - Distrito = Sede + zona Urbana → só bairros com zona 'urbana' (ou sem zona = legado)
                  - Distrito = Sede + zona Rural  → só bairros zona='rural' + comunidade + localidade_rural
                  - Distrito ≠ Sede               → TODOS os filhos (urbanos e rurais no mesmo select) */}
            {businessForm.distrito_id && (() => {
              const isSede = businessForm.is_sede === true;
              const zona = businessForm.tipo_localizacao;
              let opcoes = [];
              if (isSede && zona === 'area_urbana') {
                opcoes = bairrosTerritorios.filter((b) => b.tipo === 'bairro' && (!b.zona || b.zona === 'urbana'));
              } else if (isSede && zona === 'comunidade_rural') {
                opcoes = bairrosTerritorios.filter((b) => {
                  if (b.tipo === 'comunidade' || b.tipo === 'localidade_rural') return true;
                  if (b.tipo === 'bairro' && b.zona === 'rural') return true;
                  return false;
                });
              } else {
                // Distrito não-sede: mostra tudo (urbanos + rurais)
                opcoes = bairrosTerritorios;
              }
              if (opcoes.length === 0) {
                // Diferenciar "ainda carregando" vs "não há bairros cadastrados".
                // Se territorios ainda não chegou OU a Sede acabou de ser
                // selecionada e o fetch está em voo, mostra loading em vez de
                // "não há bairros" (que confunde o usuário).
                const isLoading = (
                  (bairrosTerritorios.length === 0 && businessForm.distrito_id) &&
                  (!Array.isArray(territorios) || territorios.length === 0)
                );
                if (isLoading) {
                  return (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex items-center gap-2" data-testid="bairros-loading">
                      <div className="w-4 h-4 rounded-full border-2 border-slate-400 border-t-transparent animate-spin" />
                      Carregando bairros...
                    </div>
                  );
                }
                return (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="no-bairros-warning">
                    Não há bairros/comunidades cadastrados neste distrito.
                    Peça à prefeitura para cadastrar em <strong>Painel Admin → Territórios</strong>.
                  </div>
                );
              }
              const label = zona === 'comunidade_rural' ? 'Comunidade / Bairro rural' : 'Bairro';
              return (
                <FormSelect
                  label={label}
                  value={businessForm.territory_id}
                  onChange={(e) => {
                    const selected = opcoes.find((b) => b.id === e.target.value);
                    setBusinessForm((prev) => ({
                      ...prev,
                      territory_id: e.target.value,
                      bairro: selected?.nome || '',
                      comunidade: (selected?.tipo === 'comunidade' || selected?.tipo === 'localidade_rural') ? (selected?.nome || '') : '',
                    }));
                  }}
                  required
                  data-testid="bairro-territory-select"
                >
                  <option value="">Selecione</option>
                  {opcoes.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nome}
                      {b.tipo === 'comunidade' ? ' (Comunidade)' : ''}
                      {b.tipo === 'localidade_rural' ? ' (Localidade Rural)' : ''}
                      {b.tipo === 'bairro' && b.zona === 'rural' ? ' (Rural)' : ''}
                    </option>
                  ))}
                </FormSelect>
              );
            })()}

            {/* Ponto de referência — opcional em zona rural (comunidades
                geralmente não têm endereço formal, mas o campo ajuda quem
                for procurar). */}
            {businessForm.tipo_localizacao === 'comunidade_rural' && (
              <FormInput
                label="Ponto de referência (opcional)"
                type="text"
                value={businessForm.ponto_referencia}
                onChange={(e) => setBusinessForm({ ...businessForm, ponto_referencia: e.target.value })}
                placeholder="Ex: Próximo à Igreja São Sebastião"
                data-testid="ponto-referencia-input"
              />
            )}

            {/* Botão "Usar minha localização" — só em zona rural */}
            {businessForm.tipo_localizacao === 'comunidade_rural' && (
              <button
                type="button"
                onClick={handleUsarLocalizacaoAtual}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-[var(--primary)]/40 text-[var(--primary)] hover:bg-[var(--primary)]/5 font-medium text-sm"
                data-testid="usar-localizacao-atual-btn"
              >
                <MapPin className="w-4 h-4" /> Usar minha localização atual
              </button>
            )}
            {ruralGeoMsg && businessForm.tipo_localizacao === 'comunidade_rural' && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="rural-geo-msg">
                {ruralGeoMsg}
              </div>
            )}

            {/* CEP — obrigatório em Urbana/Distrito, opcional em Rural.
                Comunidades rurais frequentemente compartilham o CEP default
                da cidade (que não passa em validação de formato tradicional). */}
            <CepInput
              value={businessForm.cep}
              onChange={(v) => setBusinessForm((prev) => ({ ...prev, cep: v }))}
              onAutoFill={({ logradouro, bairro }) => {
                setBusinessForm((prev) => ({
                  ...prev,
                  rua: logradouro || prev.rua,
                  bairro: bairro || prev.bairro,
                }));
              }}
              required={businessForm.tipo_localizacao !== 'comunidade_rural'}
              testId="business-cep-input"
            />

            {['area_urbana', 'distrito', 'comunidade_rural'].includes(businessForm.tipo_localizacao) && (
            <AddressInput
              bairroId={businessForm.territory_id}
              bairroNome={businessForm.bairro}
              municipioNome={municipios.find(m => m.id === businessForm.municipio_id)?.nome || ''}
              municipioId={businessForm.municipio_id}
              cep={businessForm.cep}
              municipioCenter={(() => {
                const m = municipios.find(m => m.id === businessForm.municipio_id);
                const c = m?.coordenadas || m?.centroide;
                if (c && Number.isFinite(c.lat) && Number.isFinite(c.lng)) return c;
                if (Number.isFinite(m?.latitude) && Number.isFinite(m?.longitude)) return { lat: m.latitude, lng: m.longitude };
                return null;
              })()}
              rua={businessForm.rua}
              numero={businessForm.numero}
              complemento={businessForm.complemento}
              latitude={businessForm.latitude}
              longitude={businessForm.longitude}
              onChangeRua={(v) => setBusinessForm({ ...businessForm, rua: v })}
              onChangeNumero={(v) => setBusinessForm({ ...businessForm, numero: v })}
              onChangeComplemento={(v) => setBusinessForm({ ...businessForm, complemento: v })}
              onChangeCoords={({latitude, longitude}) => setBusinessForm({ ...businessForm, latitude, longitude, geocodificacao_origem: 'manual' })}
              // Em zona rural o endereço formal (rua+número) é opcional e o
              // botão "Buscar localização pelo endereço" não faz sentido —
              // a referência de localização é o nome do bairro/comunidade.
              // Escondemos o botão mas mantemos os inputs de rua/número
              // visíveis (opcionais) caso o(a) empreendedor(a) queira preencher.
              hideGeocodeButton={businessForm.tipo_localizacao === 'comunidade_rural'}
              ruaOptional={businessForm.tipo_localizacao === 'comunidade_rural'}
            />
            )}

            {/* Fallback quando geocoding falha: alerta destacado + botão de
                localização atual. Aparece quando lat/lng ficaram sem valor
                válido (usuário só verá isso se algo deu errado). */}
            {(!Number.isFinite(Number(businessForm.latitude)) || Number(businessForm.latitude) === 0) && (
              <div
                className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3"
                data-testid="location-fallback-banner"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900">
                      Precisamos das coordenadas do seu negócio
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      A busca automática pelo endereço não achou o ponto exato. Você tem 3 formas
                      de posicionar manualmente:
                    </p>
                    <ul className="list-decimal list-inside text-xs text-amber-800 mt-2 space-y-0.5 ml-1">
                      <li>Clicar no ponto certo do mapa abaixo</li>
                      <li>Arrastar o marcador vermelho até o local exato</li>
                      <li>Usar o botão &quot;Usar minha localização atual&quot; se você estiver no negócio agora</li>
                    </ul>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleUsarLocalizacaoAtual}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-all"
                  data-testid="usar-localizacao-atual-btn"
                >
                  <Navigation className="w-4 h-4" />
                  Usar minha localização atual (GPS)
                </button>
              </div>
            )}

            {/* Item #4: helper sobre importância do endereço/localização correta */}
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2"
              data-testid="address-helper-text"
            >
              <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 leading-relaxed">
                É importante que seu endereço e localização estejam preenchidos corretamente para que os consumidores encontrem o seu negócio.
              </p>
            </div>
          </div>
        );

      // Step 7: Comodidades
      case 7:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Check className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Comodidades (Amenities)</h3>
                <p className="text-sm text-[var(--text-secondary)]">O que seu espaço oferece</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CheckboxItem
                id="acessibilidade"
                label="Acessibilidade para cadeirantes"
                icon="♿"
                checked={businessForm.acessibilidade}
                onChange={(e) => setBusinessForm({ ...businessForm, acessibilidade: e.target.checked })}
              />
              <CheckboxItem
                id="banheiro_publico"
                label="Banheiro público"
                icon="🚻"
                checked={businessForm.banheiro_publico}
                onChange={(e) => setBusinessForm({ ...businessForm, banheiro_publico: e.target.checked })}
              />
              <CheckboxItem
                id="wifi_gratis"
                label="Wi-Fi grátis"
                icon="📶"
                checked={businessForm.wifi_gratis}
                onChange={(e) => setBusinessForm({ ...businessForm, wifi_gratis: e.target.checked })}
              />
              <CheckboxItem
                id="pet_friendly"
                label="Pet friendly"
                icon="🐕"
                checked={businessForm.pet_friendly}
                onChange={(e) => setBusinessForm({ ...businessForm, pet_friendly: e.target.checked })}
              />
              <CheckboxItem
                id="espaco_kids"
                label="Espaço kids"
                icon="👶"
                checked={businessForm.espaco_kids}
                onChange={(e) => setBusinessForm({ ...businessForm, espaco_kids: e.target.checked })}
              />
              <CheckboxItem
                id="ar_condicionado"
                label="Ar-condicionado"
                icon="❄️"
                checked={businessForm.ar_condicionado}
                onChange={(e) => setBusinessForm({ ...businessForm, ar_condicionado: e.target.checked })}
              />
              <CheckboxItem
                id="area_externa"
                label="Área externa"
                icon="🌳"
                checked={businessForm.area_externa}
                onChange={(e) => setBusinessForm({ ...businessForm, area_externa: e.target.checked })}
              />
              <CheckboxItem
                id="seguranca"
                label="Segurança / Monitoramento"
                icon="🔒"
                checked={businessForm.seguranca}
                onChange={(e) => setBusinessForm({ ...businessForm, seguranca: e.target.checked })}
              />
            </div>

            <FormSelect
              label="Estacionamento"
              value={businessForm.estacionamento}
              onChange={(e) => setBusinessForm({ ...businessForm, estacionamento: e.target.value })}
            >
              <option value="">Selecione</option>
              <option value="proprio">Próprio</option>
              <option value="conveniado">Conveniado</option>
              <option value="rua">Na rua</option>
              <option value="nenhum">Não possui</option>
            </FormSelect>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <CheckboxItem
                id="entrega"
                label="Faz entrega"
                icon="🛵"
                checked={businessForm.entrega}
                onChange={(e) => setBusinessForm({ ...businessForm, entrega: e.target.checked })}
              />
              <CheckboxItem
                id="atende_domicilio"
                label="Atende em domicílio"
                icon="🏠"
                checked={businessForm.atende_domicilio}
                onChange={(e) => setBusinessForm({ ...businessForm, atende_domicilio: e.target.checked })}
              />
              <CheckboxItem
                id="promocao"
                label="Promoção ativa"
                icon="🏷️"
                checked={businessForm.promocao}
                onChange={(e) => setBusinessForm({ ...businessForm, promocao: e.target.checked })}
              />
            </div>

            {/* Faixa de Preço */}
            <div className="mt-6">
              <label className="block text-sm font-medium mb-2">
                Faixa de Preço
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: '$', label: 'Econômico', desc: 'Preços acessíveis' },
                  { value: '$$', label: 'Moderado', desc: 'Preço médio' },
                  { value: '$$$', label: 'Premium', desc: 'Produtos/serviços de alto padrão' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setBusinessForm({ ...businessForm, faixa_preco: option.value })}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      businessForm.faixa_preco === option.value
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                        : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                    }`}
                  >
                    <div className="text-2xl font-bold text-[var(--primary)]">{option.value}</div>
                    <div className="font-medium text-sm">{option.label}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Item #1: Link de delivery / marketplace — sempre disponível (iFood,
                Rappi, Goomer, App Store, Google Play, site próprio, etc.).
                Este é um LINK EXTERNO independente do módulo interno de delivery,
                por isso NÃO é gated pela feature `delivery_enabled` do município. */}
            <FormInput
              label="Link de delivery"
              placeholder="Ex: https://ifood.com.br/seu-restaurante"
              value={businessForm.link_delivery}
              onChange={(e) => setBusinessForm({ ...businessForm, link_delivery: e.target.value })}
              data-testid="business-link-delivery-input"
            />
            <p className="text-xs text-[var(--text-muted)] -mt-4">
              Cole o link do seu cardápio no iFood, Rappi, app próprio ou loja na App Store/Play Store. Aparece como botão &quot;Pedir delivery&quot; na página pública.
            </p>
          </div>
        );

      // Step 8: Pagamentos
      case 8:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <CreditCard className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Pagamentos Aceitos</h3>
                <p className="text-sm text-[var(--text-secondary)]">Formas de pagamento disponíveis</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CheckboxItem
                id="aceita_dinheiro"
                label="Dinheiro"
                icon="💵"
                checked={businessForm.aceita_dinheiro}
                onChange={(e) => setBusinessForm({ ...businessForm, aceita_dinheiro: e.target.checked })}
              />
              <CheckboxItem
                id="aceita_cartao_credito"
                label="Cartão de crédito"
                icon="💳"
                checked={businessForm.aceita_cartao_credito}
                onChange={(e) => setBusinessForm({ ...businessForm, aceita_cartao_credito: e.target.checked })}
              />
              <CheckboxItem
                id="aceita_cartao_debito"
                label="Cartão de débito"
                icon="💳"
                checked={businessForm.aceita_cartao_debito}
                onChange={(e) => setBusinessForm({ ...businessForm, aceita_cartao_debito: e.target.checked })}
              />
              <CheckboxItem
                id="aceita_pix"
                label="PIX"
                icon="📱"
                checked={businessForm.aceita_pix}
                onChange={(e) => setBusinessForm({ ...businessForm, aceita_pix: e.target.checked })}
              />
              <CheckboxItem
                id="aceita_convenios"
                label="Convênios de saúde"
                icon="🏥"
                checked={businessForm.aceita_convenios}
                onChange={(e) => setBusinessForm({ ...businessForm, aceita_convenios: e.target.checked })}
              />
            </div>
            
            {/* Campo de descrição de convênios */}
            {businessForm.aceita_convenios && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Quais convênios/entidades atendidas?
                </label>
                <input
                  type="text"
                  value={businessForm.convenios_descricao || ''}
                  onChange={(e) => setBusinessForm({ ...businessForm, convenios_descricao: e.target.value })}
                  placeholder="Ex: Unimed, Bradesco Saúde, Sulamerica..."
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                />
              </div>
            )}
          </div>
        );

      // Step 9: Diferenciais
      case 9:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Heart className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Diferenciais do Negócio</h3>
                <p className="text-sm text-[var(--text-secondary)]">O que torna seu negócio especial</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CheckboxItem
                id="producao_artesanal"
                label="Produção artesanal / local"
                icon="🎨"
                checked={businessForm.producao_artesanal}
                onChange={(e) => setBusinessForm({ ...businessForm, producao_artesanal: e.target.checked })}
              />
              <CheckboxItem
                id="produtos_sustentaveis"
                label="Produtos sustentáveis / ecológicos"
                icon="🌿"
                checked={businessForm.produtos_sustentaveis}
                onChange={(e) => setBusinessForm({ ...businessForm, produtos_sustentaveis: e.target.checked })}
              />
              <CheckboxItem
                id="cooperativa"
                label="Pertence a cooperativa / associação"
                icon="🤝"
                checked={businessForm.cooperativa}
                onChange={(e) => setBusinessForm({ ...businessForm, cooperativa: e.target.checked })}
              />
              <CheckboxItem
                id="empresa_familiar"
                label="Empresa familiar / comunitária"
                icon="👨‍👩‍👧‍👦"
                checked={businessForm.empresa_familiar}
                onChange={(e) => setBusinessForm({ ...businessForm, empresa_familiar: e.target.checked })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Certificações (orgânico, ISO, ANVISA, selo municipal)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {businessForm.certificacoes.map((cert, index) => (
                  <span
                    key={`${cert}-${index}`}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {cert}
                    <button
                      type="button"
                      onClick={() => setBusinessForm(prev => ({
                        ...prev,
                        certificacoes: prev.certificacoes.filter((_, i) => i !== index)
                      }))}
                      className="ml-1 hover:text-green-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Digite uma certificação"
                  className="flex-1 px-4 py-2 rounded-xl border border-[var(--border)]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value && !businessForm.certificacoes.includes(value)) {
                        setBusinessForm(prev => ({
                          ...prev,
                          certificacoes: [...prev.certificacoes, value]
                        }));
                        e.target.value = '';
                      }
                    }
                  }}
                />
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">Pressione Enter para adicionar</p>
            </div>
          </div>
        );

      // Step 10: Social & Impacto + Contratação
      case 10:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Heart className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Social & Impacto</h3>
                <p className="text-sm text-[var(--text-secondary)]">Seu impacto na comunidade</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CheckboxItem
                id="projetos_sociais"
                label="Participa de projetos sociais"
                icon="🤲"
                checked={businessForm.projetos_sociais}
                onChange={(e) => setBusinessForm({ ...businessForm, projetos_sociais: e.target.checked })}
              />
              <CheckboxItem
                id="emprega_comunidade"
                label="Emprega mão de obra local"
                icon="👷"
                checked={businessForm.emprega_comunidade}
                onChange={(e) => setBusinessForm({ ...businessForm, emprega_comunidade: e.target.checked })}
              />
              <CheckboxItem
                id="trabalha_inclusao"
                label="Trabalha com inclusão"
                icon="♿"
                checked={businessForm.trabalha_inclusao}
                onChange={(e) => setBusinessForm({ ...businessForm, trabalha_inclusao: e.target.checked })}
              />
              <CheckboxItem
                id="apoia_cultura"
                label="Apoia artistas / cultura local"
                icon="🎭"
                checked={businessForm.apoia_cultura}
                onChange={(e) => setBusinessForm({ ...businessForm, apoia_cultura: e.target.checked })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Iniciativas sustentáveis (reciclagem, energia solar, etc.)
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {businessForm.iniciativas_sustentaveis.map((init, index) => (
                  <span
                    key={`${init}-${index}`}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {init}
                    <button
                      type="button"
                      onClick={() => setBusinessForm(prev => ({
                        ...prev,
                        iniciativas_sustentaveis: prev.iniciativas_sustentaveis.filter((_, i) => i !== index)
                      }))}
                      className="ml-1 hover:text-green-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                placeholder="Digite uma iniciativa e pressione Enter"
                className="w-full px-4 py-2 rounded-xl border border-[var(--border)]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = e.target.value.trim();
                    if (value && !businessForm.iniciativas_sustentaveis.includes(value)) {
                      setBusinessForm(prev => ({
                        ...prev,
                        iniciativas_sustentaveis: [...prev.iniciativas_sustentaveis, value]
                      }));
                      e.target.value = '';
                    }
                  }
                }}
              />
            </div>
          </div>
        );

      // Step 11: Imagens
      case 11:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Camera className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Imagens do Negócio</h3>
                <p className="text-sm text-[var(--text-secondary)]">
                  {createdBusinessId
                    ? 'Envie o logo e fotos da galeria. Você pode pular e adicionar depois em "Editar Negócio".'
                    : 'Salvando rascunho do negócio para liberar o upload das fotos...'}
                </p>
              </div>
            </div>

            {creatingDraft && (
              <div className="flex items-center gap-2 text-[var(--primary)] bg-[var(--primary)]/5 rounded-xl p-3">
                <div className="w-4 h-4 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Salvando rascunho do negócio para liberar o upload de imagens...</span>
              </div>
            )}

            {createdBusinessId && (
              <>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                  Rascunho criado com sucesso. Suas alterações nas próximas etapas (Gestão, Termos)
                  serão aplicadas ao final.
                </div>

                <ImageUploader
                  area="negocio"
                  tipo="logo"
                  formato="quadrado"
                  entidadeId={createdBusinessId}
                  municipioId={businessForm.municipio_id}
                  imagensAtuais={businessForm.imagens?.logo || null}
                  single
                  label="Logo do negócio"
                  description="Recomendamos imagem no formato quadrado — sugestão: 1080×1080 px"
                  onChange={(imgs) => {
                    setBusinessForm((prev) => ({
                      ...prev,
                      imagens: { ...prev.imagens, logo: imgs[0] || null },
                      avatar_url: imgs.length === 0 ? '' : (imgs[0]?.medium || prev.avatar_url),
                    }));
                  }}
                />

                <ImageUploader
                  area="negocio"
                  tipo="galeria"
                  formato="quadrado"
                  entidadeId={createdBusinessId}
                  municipioId={businessForm.municipio_id}
                  imagensAtuais={businessForm.imagens?.galeria || []}
                  label="Galeria de fotos"
                  description="Adicione fotos da fachada, ambiente, produtos, equipe e clientes"
                  onChange={(imgs) => {
                    setBusinessForm((prev) => ({
                      ...prev,
                      imagens: { ...prev.imagens, galeria: imgs },
                    }));
                  }}
                />
              </>
            )}

            {!creatingDraft && !createdBusinessId && (
              <button
                type="button"
                onClick={ensureBusinessExists}
                className="w-full py-3 rounded-xl border-2 border-dashed border-[var(--primary)] text-[var(--primary)] font-semibold hover:bg-[var(--primary)]/5"
                data-testid="retry-draft-create-btn"
              >
                Tentar salvar rascunho novamente
              </button>
            )}
          </div>
        );

      // Step 12: Gestão do Cadastro
      case 12:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Briefcase className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Gestão do Cadastro</h3>
                <p className="text-sm text-[var(--text-secondary)]">Defina sua relação com este negócio</p>
              </div>
            </div>

            {/* Papel no cadastro */}
            <div>
              <label className="block text-sm font-medium mb-3">
                Estou cadastrando como: <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: 'OWNER', label: 'Proprietário', desc: 'Sou dono/sócio do negócio' },
                  { value: 'REPRESENTATIVE', label: 'Representante', desc: 'Represento o negócio' },
                  { value: 'COLLABORATOR', label: 'Colaborador', desc: 'Ajudo a gerenciar' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      businessForm.manager_role === option.value
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                        : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="manager_role"
                      value={option.value}
                      checked={businessForm.manager_role === option.value}
                      onChange={(e) => setBusinessForm({ ...businessForm, manager_role: e.target.value })}
                      className="sr-only"
                    />
                    <div className="font-medium text-sm">{option.label}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-1">{option.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            {/* Relação com o negócio */}
            <div>
              <label className="block text-sm font-medium mb-3">
                Qual sua relação com o negócio? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { value: 'PROPRIETARIO', label: 'Proprietário' },
                  { value: 'FUNCIONARIO', label: 'Funcionário' },
                  { value: 'AGENCIA', label: 'Agência/Marketing' },
                  { value: 'ASSOCIACAO', label: 'Associação' },
                  { value: 'FAMILIAR', label: 'Familiar' },
                  { value: 'CONTADOR', label: 'Contador' },
                  { value: 'OUTRO', label: 'Outro' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all text-center ${
                      businessForm.manager_relation === option.value
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                        : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="manager_relation"
                      value={option.value}
                      checked={businessForm.manager_relation === option.value}
                      onChange={(e) => setBusinessForm({ ...businessForm, manager_relation: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Descrição se OUTRO */}
            {businessForm.manager_relation === 'OUTRO' && (
              <FormInput
                label="Descreva sua relação"
                required
                value={businessForm.manager_relation_description}
                onChange={(e) => setBusinessForm({ ...businessForm, manager_relation_description: e.target.value })}
                placeholder="Ex: Consultor, Parceiro comercial..."
              />
            )}

            {/* Termo de responsabilidade */}
            <div className={`rounded-xl p-4 transition-all ${
              businessForm.terms_manager_accepted 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border-2 border-red-300 animate-pulse'
            }`}>
              <label
                htmlFor="terms_manager_accepted"
                className="flex items-start gap-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="terms_manager_accepted"
                  checked={businessForm.terms_manager_accepted}
                  onChange={(e) => setBusinessForm({ ...businessForm, terms_manager_accepted: e.target.checked })}
                  className="w-6 h-6 mt-0.5 rounded border-2 border-gray-400 text-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  data-testid="terms-manager-accepted"
                />
                <div className="flex-1">
                  <span className={`text-sm font-bold ${
                    businessForm.terms_manager_accepted ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {businessForm.terms_manager_accepted ? '✓ ' : '⚠️ '}
                    Declaro que tenho autorização para cadastrar e gerenciar este negócio
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <p className={`text-xs mt-1 ${
                    businessForm.terms_manager_accepted ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {businessForm.terms_manager_accepted 
                      ? 'Obrigado! Você pode avançar para a próxima etapa.'
                      : 'Marque esta opção para continuar. Você assume responsabilidade pelas informações.'
                    }
                  </p>
                </div>
              </label>
            </div>

            {/* Informativo */}
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-[var(--text-secondary)]">
              <p className="font-medium mb-2">📋 Sobre a gestão do cadastro:</p>
              <ul className="space-y-1 text-xs">
                <li>• <strong>Proprietário:</strong> Controle total, pode adicionar outros gestores</li>
                <li>• <strong>Representante:</strong> Pode editar todas as informações</li>
                <li>• <strong>Colaborador:</strong> Edição limitada, sob supervisão do proprietário</li>
              </ul>
              <p className="mt-2 text-xs">
                O tipo de gestão será exibido publicamente (ex: "Cadastro gerenciado pelo proprietário").
              </p>
            </div>
          </div>
        );

      // Step 13: Termos e Consentimentos
      case 13:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-[var(--primary)]/10 rounded-xl">
                <Check className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Termos e Consentimentos</h3>
                <p className="text-sm text-[var(--text-secondary)]">Leia e aceite os termos para finalizar</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Política de Privacidade - Obrigatório */}
              <label
                htmlFor="aceita_politica_privacidade"
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  businessForm.aceita_politica_privacidade
                    ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                    : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                }`}
              >
                <input
                  type="checkbox"
                  id="aceita_politica_privacidade"
                  checked={businessForm.aceita_politica_privacidade}
                  onChange={(e) => setBusinessForm({ ...businessForm, aceita_politica_privacidade: e.target.checked })}
                  className="w-5 h-5 mt-0.5 rounded border-[var(--border)] text-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  data-testid="aceita-politica-privacidade"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium">
                    Li e aceito a <a href="/politica-de-privacidade" target="_blank" className="text-[var(--primary)] hover:underline">Política de Privacidade</a> (LGPD)
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Seus dados serão tratados conforme a Lei Geral de Proteção de Dados
                  </p>
                </div>
              </label>

              {/* Termos de Uso - Obrigatório */}
              <label
                htmlFor="aceita_termos_uso"
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  businessForm.aceita_termos_uso
                    ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                    : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                }`}
              >
                <input
                  type="checkbox"
                  id="aceita_termos_uso"
                  checked={businessForm.aceita_termos_uso}
                  onChange={(e) => setBusinessForm({ ...businessForm, aceita_termos_uso: e.target.checked })}
                  className="w-5 h-5 mt-0.5 rounded border-[var(--border)] text-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  data-testid="aceita-termos-uso"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium">
                    Li e aceito os <a href="/termos-de-uso" target="_blank" className="text-[var(--primary)] hover:underline">Termos de Uso da Plataforma</a>
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Regras e condições para uso da plataforma Empreende Comunidade
                  </p>
                </div>
              </label>

              {/* Comunicações - Opcional (opt-in) */}
              <label
                htmlFor="aceita_comunicacoes"
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  businessForm.aceita_comunicacoes
                    ? 'border-green-500 bg-green-50'
                    : 'border-[var(--border)] hover:border-green-500/50'
                }`}
              >
                <input
                  type="checkbox"
                  id="aceita_comunicacoes"
                  checked={businessForm.aceita_comunicacoes}
                  onChange={(e) => setBusinessForm({ ...businessForm, aceita_comunicacoes: e.target.checked })}
                  className="w-5 h-5 mt-0.5 rounded border-[var(--border)] text-green-600 focus:ring-2 focus:ring-green-500/20"
                  data-testid="aceita-comunicacoes"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium">
                    Desejo receber conteúdos relevantes para empreendedores
                  </span>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Informativos municipais, dicas de negócios e novidades de parceiros (opcional)
                  </p>
                </div>
              </label>
            </div>

            {/* Warning if required fields not checked */}
            {(!businessForm.aceita_politica_privacidade || !businessForm.aceita_termos_uso) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Você precisa aceitar a Política de Privacidade e os Termos de Uso para cadastrar seu negócio.
                </p>
              </div>
            )}

            {/* Success message when all required are checked */}
            {businessForm.aceita_politica_privacidade && businessForm.aceita_termos_uso && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">
                  Tudo certo! Clique em "Cadastrar negócio" para finalizar.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20 pt-8">
      <SEOHead title="Cadastrar Negócio" noindex={true} />

      {/* Server-side draft recovery modal (iter254) — evita duplicatas quando
          o usuário abre /cadastrar em outro device e o localStorage está vazio. */}
      {serverDraftOffer && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="server-draft-modal"
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-2">Você já começou um cadastro antes</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Encontramos um rascunho no servidor:
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4 border">
              <div className="font-medium">{serverDraftOffer.nome || '(sem nome ainda)'}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">
                {serverDraftOffer.categoria || 'sem categoria'}
                {serverDraftOffer.updated_at && (
                  <> · atualizado em {new Date(serverDraftOffer.updated_at).toLocaleDateString('pt-BR')}</>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  const id = serverDraftOffer.id;
                  setServerDraftOffer(null);
                  navigate(`/cadastrar?continue=${id}`, { replace: true });
                  window.location.reload();
                }}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full py-3 font-medium"
                data-testid="continue-server-draft-btn"
              >
                Continuar esse cadastro
              </button>
              <button
                type="button"
                onClick={() => setServerDraftOffer(null)}
                className="w-full border border-gray-300 hover:bg-gray-50 rounded-full py-3 font-medium text-sm"
                data-testid="dismiss-server-draft-btn"
              >
                Começar do zero (o rascunho antigo continuará salvo)
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
            Cadastrar negócio
          </h1>
          <p className="text-[var(--text-secondary)] mb-6">
            Preencha as informações do seu negócio
          </p>

          <StepIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
            stepLabels={stepLabels}
            onStepClick={(step) => setCurrentStep(step)}
          />

          <form onSubmit={handleBusinessSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 mb-8 pt-6 border-t border-[var(--border)]">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                  currentStep === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-[var(--primary)] hover:bg-[var(--primary)]/10'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
                Anterior
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={creatingDraft}
                  data-testid="next-step-button"
                  title={
                    !validateCurrentStep()
                      ? `Preencha: ${getMissingFields().join(', ')}`
                      : ''
                  }
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                    creatingDraft
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : validateCurrentStep()
                        ? 'bg-[var(--primary)] text-white hover:bg-[#450066]'
                        // Botão "cinza acessível" quando step inválido — mostra
                        // ao usuário que o próximo está bloqueado, mas mantém
                        // clicável para exibir a mensagem de erro (toast + painel).
                        : 'bg-gray-300 text-gray-600 hover:bg-gray-400 cursor-help'
                  }`}
                >
                  {creatingDraft ? 'Salvando rascunho...' : 'Próximo'}
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 rounded-xl px-8 py-3 font-bold transition-all shadow-lg active:scale-95 disabled:opacity-50 ${
                    !loading
                      ? 'bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[#FF9500] hover:shadow-xl'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  data-testid="business-submit-button"
                >
                  {loading ? 'Cadastrando...' : 'Cadastrar negócio'}
                  <Check className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Feedback explícito do que falta — aparece ao tentar avançar sem preencher. */}
            {showValidationErrors && getMissingFields().length > 0 && (
              <div
                id="step-validation-errors"
                className="mt-4 rounded-xl border-2 border-red-300 bg-red-50 p-4 flex gap-2"
                data-testid="step-missing-fields"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800 leading-relaxed">
                  <strong>Para continuar, preencha os campos obrigatórios:</strong>
                  <ul className="list-disc pl-4 mt-1 space-y-0.5">
                    {getMissingFields().map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
